<template>
  <div class="bookmarks">
    <h2>Bookmarks</h2>

    <p>
      You can bookmark fandoms, freeforms, relationships and - when they become available - individual
      prompts by clicking on the
      <span class="far fa-heart"></span> icon. This data is saved in your
      browser, and will remain for as long as you do not clear the cache.
    </p>
    <p>
      You can resize columns on the tables by dragging/dropping the bar on the
      column header. You can drag/drop using the
      <span class="fas fa-arrows-alt-v move"></span> to re-order things as you
      like.
    </p>

    <div class="options accordion">
      <input type="checkbox" id="accordion-for-backup" name="accordion-checkbox" v-model="showbackup" hidden />
      <label class="accordion-header mx-0 c-hand text-primary" for="accordion-for-backup">
        <i class="icon icon-arrow-right mr-1"></i>
        <strong>{{ showbackup ? "Hide" : "Show" }} Backup Instructions</strong>
      </label>
      <div class="accordion-body">
        <p class="mt-2">
          To transfer your bookmarks between browsers/computers or to back them
          up, copy
          <em>exactly</em>
          the text in the "Copy" box. To load them, paste the text nto the
          "Load" box and hit Load. Note that bookmarks that you load in will be
          <strong>merged</strong> with any existing bookmarks you have in the
          app that you're loading them into. You <strong>do not</strong> have to
          do this if you use the same browser every time.
        </p>

        <div class="columns">
          <div class="column col-6">
            <strong>Copy:</strong>
            <textarea cols="10" rows="2" v-model="marks"></textarea>
          </div>
          <div class="column col-6">
            <strong>Load:</strong>
            <textarea cols="10" rows="2" v-model="saved"></textarea>
          </div>
        </div>
        <div class="my-2">
          <button class="btn" @click="loadSaved">Load!</button>
        </div>
        <hr />
      </div>
    </div>

    <div class="fandoms">
      <h3>
        Fandoms
        <span :class="[
          'fas',
          showFandomsSection ? 'fa-angle-down' : 'fa-angle-right',
        ]" @click="showFandomsSection = !showFandomsSection"></span>
      </h3>
      <template v-if="showFandomsSection">
        <label class="form-checkbox form-inline">
          <input type="checkbox" v-model="hideCharacters" />
          <i class="form-icon"></i> Hide Tagset Characters & Relationships column
        </label>
        <table v-table-resizer v-if="bookmarks && bookmarks.length">
          <thead>
            <tr>
              <th class="fandom">Fandom</th>
              <th class="characters" v-if="!hideCharacters">
                Tagset Characters & Relationships
              </th>
              <th class="prompts">Prompts</th>
            </tr>
          </thead>
          <tbody v-model="bookmarks" is="draggable" tag="tbody" handle=".move">
            <tr v-for="(fandom, index) in bookmarks" :class="{ odd: index % 2 !== 0 }" :key="`${fandom.name}-${index}`">
              <td class="fandom">
                <span class="fas fa-arrows-alt-v move"></span>
                <router-link :to="`/${exchangeId}/fandom/${fandom.name}`">
                  {{ fandom.name }} </router-link>&nbsp;
                <span @click="remove(fandom)" class="remove far fa-times-circle"></span>
                &nbsp;
                <div class="meta">
                  <span class="category meta-tag" v-if="!options.hideCategory">
                    {{ fandom.categories ? fandom.categories.join(", ") : "" }}
                  </span>
                </div>
              </td>

              <td class="characters" v-if="!hideCharacters">
                <ul>
                  <li v-for="char in getCharacters(fandom.index, fandom.name)" :key="char">
                    {{ char }}
                  </li>
                </ul>
              </td>
              <td class="prompts">
                <button class="btn btn-primary"
                  v-if="!prompts[fandom.index] && hasPrompts.length && hasPrompts[fandom.index] && hasPrompts[fandom.index].has_prompts"
                  @click="getPrompts(exchangeId, fandom.index, fandom.name)">
                  Get Requests
                </button>
                <div v-if="prompts[fandom.index] === 'loading'">
                  Loading...
                </div>
                <template v-else-if="prompts[fandom.index] &&
                  prompts[fandom.index].length &&
                  prompts[fandom.index] !== 'loading'
                ">
                  <Prompt-Table :prompts="prompts[fandom.index]"></Prompt-Table>
                </template>
                <span v-if="!hasPrompts[fandom.index] || !hasPrompts[fandom.index].has_prompts">No prompts ):</span>
              </td>
            </tr>
          </tbody>
        </table>

        <div v-else>You haven't bookmarked any fandoms yet ):</div>
      </template>
      <div class="divider"></div>
    </div>
    <!-- asdsad -->
    <div class="freeforms">
      <h3>
        Freeforms
        <span :class="[
          'fas',
          showFreeformsSection ? 'fa-angle-down' : 'fa-angle-right',
        ]" @click="showFreeformsSection = !showFreeformsSection"></span>
      </h3>
      <template v-if="showFreeformsSection">

        <table v-table-resizer v-if="freeformmarks && freeformmarks.length">
          <thead>
            <tr>
              <th class="fandom">Tag</th>
              <th class="prompts">Prompts</th>
            </tr>
          </thead>
          <tbody v-model="freeformmarks" is="draggable" tag="tbody" handle=".move">
            <tr v-for="(tag, index) in freeformmarks" :class="{ odd: index % 2 !== 0 }" :key="`${tag}-${index}`">
              <td class="fandom">
                <span class="fas fa-arrows-alt-v move"></span>
                {{ tag }}&nbsp;
                <span @click="removeFreeformmark(tag)" class="remove far fa-times-circle"></span>
                &nbsp;
              </td>
              <td class="prompts">
                <button class="btn btn-primary" v-if="!getFreeformPrompts(tag)" @click="remoteLoadFreeformPrompts(tag)">
                  Show Requests
                </button>

                <div v-if="getFreeformPrompts(tag) === 'loading'" class="loading"></div>
                <keep-alive>
                  <prompt-table :showFandom="true" :prompts="getFreeformPrompts(tag)"
                    v-if="shouldShowFreeformPrompts(tag)">
                  </prompt-table>
                </keep-alive>
              </td>
            </tr>
          </tbody>
        </table>

        <div v-else>You haven't bookmarked any freeforms yet ):</div>
      </template>
      <div class="divider"></div>
    </div>


    <!-- asdsad -->
    <div class="relationships">
      <h3>
        Relationships
        <span :class="[
          'fas',
          showRelationshipsSection ? 'fa-angle-down' : 'fa-angle-right',
        ]" @click="showRelationshipsSection = !showRelationshipsSection"></span>
      </h3>
      <template v-if="showRelationshipsSection">

        <table v-table-resizer v-if="relationshipmarks && relationshipmarks.length">
          <thead>
            <tr>
              <th class="fandom">Tag</th>
              <th class="prompts">Prompts</th>
            </tr>
          </thead>
          <tbody v-model="relationshipmarks" is="draggable" tag="tbody" handle=".move">
            <tr v-for="(tag, index) in relationshipmarks" :class="{ odd: index % 2 !== 0 }" :key="`${tag}-${index}`">
              <td class="fandom">
                <span class="fas fa-arrows-alt-v move"></span>
                {{ tag }}&nbsp;
                <span @click="removeRelationshipmark(tag)" class="remove far fa-times-circle"></span>
                &nbsp;
              </td>
              <td class="prompts">
                <button class="btn btn-primary" v-if="!getRelationshipPrompts(tag)"
                  @click="remoteLoadRelationshipPrompts(tag)">
                  Show Requests
                </button>

                <div v-if="getRelationshipPrompts(tag) === 'loading'" class="loading"></div>
                <keep-alive>
                  <prompt-table :showFandom="true" :prompts="getRelationshipPrompts(tag)"
                    v-if="shouldShowRelationshipPrompts(tag)">
                  </prompt-table>
                </keep-alive>
              </td>
            </tr>
          </tbody>
        </table>

        <div v-else>You haven't bookmarked any relationships yet ):</div>
      </template>

      <div class="divider"></div>
    </div>



    <h3 v-if="!isExchangeOpen">
      Prompts
      <span :class="[
        'fas',
        showPromptsSection ? 'fa-angle-down' : 'fa-angle-right',
      ]" @click="showPromptsSection = !showPromptsSection"></span>
    </h3>

    <template v-if="showPromptsSection && !isExchangeOpen">
      <label class="form-switch form-inline">
        <input type="checkbox" v-model="togglePrompts" />
        <i class="form-icon"></i> Use toggles for prompt text
      </label>

      <div v-if="promptmarks && promptmarks.length">
        <table v-table-resizer class="prompts">
          <thead>
            <tr>
              <th class="fave bookmark">
                <span class="fas fa-heart"></span>
              </th>
              <th class="fandom">Username/Fandom</th>
              <th class="characters">Characters & Relationships</th>
              <th class="prompts">Prompts</th>
            </tr>
          </thead>
          <tbody v-model="promptmarks" is="draggable" tag="tbody" handle=".move">
            <tr v-for="(prompt, index) in promptmarks" :key="index" :class="{ odd: index % 2 !== 0 }">
              <td class="fave">
                <button aria-label="Toggle prompt bookmark" class="bookmark" @click="togglePromptmark(prompt)">
                  <span v-if="hasPromptmark(prompt)" aria-label="Remove bookmark" class="fas fa-heart"></span>
                  <span v-else class="far fa-heart" aria-label="Bookmark"></span>
                </button>
              </td>
              <td>
                <span class="fas fa-arrows-alt-v move"></span>
                <a href="#" @click.prevent="
                  $router.push({
                    path: `/${exchangeId}/user/${prompt.username}`,
                  })
                  ">{{ prompt.username }}</a>
                <div class="divider"></div>

                <strong>{{ prompt.fandom }}</strong>
              </td>
              <td>
                <character-list :characters="prompt.characters" :relationships="prompt.relationships"
                  :hide-title="true"></character-list>
              </td>
              <td class="prompt">
                <chips v-if="prompt.fandoms && prompt.fandoms.length > 1"
                  :tags="prompt.fandoms.filter(f => f != prompt.fandom)" type="fandom" :exchangeId="exchangeId"
                  title="Other Fandoms"></chips>
                <div class="mb-2" v-if="prompt.title">
                  <strong>Title:</strong>&nbsp;<span>{{ prompt.title }}</span>
                </div>


                <chips :tags="prompt.warnings" title="Warnings"></chips>
                <chips :tags="prompt.ratings" title="Ratings"></chips>
                <chips :tags="prompt.freeforms" title="Freeforms"></chips>
                <chips :tags="prompt.optionalTags || prompt.optional_tags" title="Optional Tags"></chips>

                <button class="btn btn-link" v-if="togglePrompts" @click="togglePrompt(index)">
                  <span v-if="shouldShowPrompt(index)">
                    <i class="icon icon-arrow-down mr-1"></i> Hide details
                  </span>
                  <span v-else>
                    <i class="icon icon-arrow-right mr-1"></i> Show details
                  </span>
                </button>
                <template v-if="(!togglePrompts || shouldShowPrompt(index)) && prompt.prompt
                ">
                  <div class="mb-2" v-if="prompt.letter && prompt.letter.length">
                    <a :href="prompt.letter" target="blank">Letter </a><span class="tooltip c-hand" data-tooltip="If a request has HTML links in 
the optional details but no letter, 
the scraper interprets the last link
as a letter. Alas, this cannot be 
fixed due to the nature of AO3's
request page.">
                      <sup>
                        <i class="fa fa-exclamation-circle" aria-hidden="true"></i> </sup></span>
                  </div>
                  <div v-html="prompt.prompt" class="prompt-text"></div>
                </template>

                <p v-if="!prompt.prompt">
                  No optional details given.
                  <span v-if="prompt.letter">
                    However, they do have a
                    <a :href="prompt.letter" target="blank">letter </a><span class="tooltip c-hand" data-tooltip="If a request has HTML links in 
the optional details but no letter, 
the scraper interprets the last link
as a letter. Alas, this cannot be 
fixed due to the nature of AO3's
request page.">
                      <sup>
                        <i class="fa fa-exclamation-circle" aria-hidden="true"></i> </sup></span>.
                  </span>
                </p>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div v-else>You haven't bookmarked any prompts yet ):</div>
    </template>

    <div class="divider"></div>

    <template v-if="aggregatedPromptmarks.length">

      <h3>Aggregated Prompts <span :class="[
        'fas',
        showAggregatedPromptsSection ? 'fa-angle-down' : 'fa-angle-right',
      ]" @click="showAggregatedPromptsSection = !showAggregatedPromptsSection"></span>
      </h3>

      <div class="toast toast-warning" v-if="!updating && !processing">
        These pull from the experimental feature which lists all past requests from users who
        have opted into
        <strong>an aggregate fandom</strong> (either Creator's Choice of Previously Requested Fandom or Any Fandom I've
        Requested Before) for this exchange. <br /><br /> <strong>Please make sure to check if the user has listed any
          DNWs/exceptions in
          their <strong>current</strong>
          request.</strong>
      </div>

      <template v-if="showAggregatedPromptsSection && !isExchangeOpen">
        <label class="form-switch form-inline">
          <input type="checkbox" v-model="togglePrompts" />
          <i class="form-icon"></i> Use toggles for prompt text
        </label>

        <div>
          <table v-table-resizer class="prompts">
            <thead>
              <tr>
                <th class="fave bookmark">
                  <span class="fas fa-heart"></span>
                </th>
                <th class="fandom">Exchange/Username/Fandom</th>
                <th class="characters">Characters & Relationships</th>
                <th class="prompts">Prompts</th>
              </tr>
            </thead>
            <tbody v-model="aggregatedPromptmarks" is="draggable" tag="tbody" handle=".move">
              <tr v-for="(prompt, index) in aggregatedPromptmarks" :key="index" :class="{ odd: index % 2 !== 0 }">
                <td class="fave">
                  <button class="bookmark" @click="toggleAggregatedPromptmark(prompt)">
                    <span v-if="hasAggregatedPromptmark(prompt)" class="fas fa-heart"></span>
                    <span v-else class="far fa-heart" aria-label="Bookmark"></span>
                  </button>
                </td>
                <td>
                  <span class="fas fa-arrows-alt-v move"></span>
                  <a href="#" @click.prevent="
                    $router.push({
                      path: `/${prompt.exchange}`,
                    })
                    ">{{ prompt.exchange }}</a>
                  <div class="divider"></div>

                  <a href="#" @click.prevent="
                    $router.push({
                      path: `/${exchangeId}/user/${prompt.username}`,
                    })
                    ">{{ prompt.username }}</a>
                  <div class="divider"></div>

                  <strong>{{ prompt.fandom }}</strong>
                </td>
                <td>
                  <character-list :characters="prompt.characters" :relationships="prompt.relationships"
                    :hide-title="true"></character-list>
                </td>
                <td class="prompt">
                  <chips v-if="prompt.fandoms && prompt.fandoms.length > 1"
                    :tags="prompt.fandoms.filter(f => f != prompt.fandom)" type="fandom" :exchangeId="exchangeId"
                    title="Other Fandoms"></chips>
                  <div class="mb-2" v-if="prompt.title">
                    <strong>Title:</strong>&nbsp;<span>{{ prompt.title }}</span>
                  </div>


                  <chips :tags="prompt.warnings" title="Warnings"></chips>
                  <chips :tags="prompt.ratings" title="Ratings"></chips>
                  <chips :tags="prompt.freeforms" title="Freeforms"></chips>
                  <chips :tags="prompt.optionalTags || prompt.optional_tags" title="Optional Tags"></chips>

                  <button class="btn btn-link" v-if="togglePrompts" @click="togglePrompt(index)">
                    <span v-if="shouldShowPrompt(index)">
                      <i class="icon icon-arrow-down mr-1"></i> Hide details
                    </span>
                    <span v-else>
                      <i class="icon icon-arrow-right mr-1"></i> Show details
                    </span>
                  </button>
                  <template v-if="(!togglePrompts || shouldShowPrompt(index)) && prompt.prompt
                  ">
                    <div class="mb-2" v-if="prompt.letter && prompt.letter.length">
                      <a :href="prompt.letter" target="blank">Letter </a><span class="tooltip c-hand" data-tooltip="If a request has HTML links in 
  the optional details but no letter, 
  the scraper interprets the last link
  as a letter. Alas, this cannot be 
  fixed due to the nature of AO3's
  request page.">
                        <sup>
                          <i class="fa fa-exclamation-circle" aria-hidden="true"></i> </sup></span>
                    </div>
                    <div v-html="prompt.prompt" class="prompt-text"></div>
                  </template>

                  <p v-if="!prompt.prompt">
                    No optional details given.
                    <span v-if="prompt.letter">
                      However, they do have a
                      <a :href="prompt.letter" target="blank">letter </a><span class="tooltip c-hand" data-tooltip="If a request has HTML links in 
  the optional details but no letter, 
  the scraper interprets the last link
  as a letter. Alas, this cannot be 
  fixed due to the nature of AO3's
  request page.">
                        <sup>
                          <i class="fa fa-exclamation-circle" aria-hidden="true"></i> </sup></span>.
                    </span>
                  </p>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </template>
    </template>
  </div>
</template>

<script>
import PromptTable from "../components/promptTable";
import chips from "../components/chips.vue";
import characterList from "../components/character-list.vue";
import { compact, map, find, union } from "lodash-es";
import { mapGetters } from "vuex";
import utils from "../components/utils.js";
import draggable from "vuedraggable";
import axios from "axios";
import tableResizer from "../directives/tableResizer";
import data from "../store/data"
const cyrb53 = function (str, seed = 0) {
  if (!str) return "";
  let h1 = 0xdeadbeef ^ seed,
    h2 = 0x41c6ce57 ^ seed;
  for (let i = 0, ch; i < str.length; i++) {
    ch = str.charCodeAt(i);
    h1 = Math.imul(h1 ^ ch, 2654435761);
    h2 = Math.imul(h2 ^ ch, 1597334677);
  }
  h1 =
    Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^
    Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 =
    Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^
    Math.imul(h1 ^ (h1 >>> 13), 3266489909);
  return 4294967296 * (2097151 & h2) + (h1 >>> 0);
};

export default {
  components: {
    PromptTable,
    chips,
    characterList,
    draggable
  },
  directives: {
    tableResizer
  },
  async beforeMount() {
    let exchangeId = this.$route.params.exchangeId;
    await this.redirectExchangeId();


    if (!this.exchangeId) {
      this.$store.commit("setExchangeId", exchangeId);
      this.$store.commit("setCharsLoaded", false);
      this.$store.commit("setDbLoaded", false);

    }


    this.loadFandomBookmarksAndPromptmarks();

    axios
      .get(
        `https://autoao3.herokuapp.com/db/exchanges/${exchangeId}/tagset/hasprompts`
      )
      .then(async (res) => {
        let data = res.data;

        // Auctionbot
        if (exchangeId == 'AuctionsAsThreatened' || exchangeId == 'AAT_Request_Overflow') {
          const otherExchange = exchangeId == 'AuctionsAsThreatened' ? 'AAT_Request_Overflow' : 'AuctionsAsThreatened';
          axios
          let overflowReq = await axios
            .get(`https://autoao3.herokuapp.com/db/exchanges/${otherExchange}/tagset/hasprompts`);
          let overflow = overflowReq.data;
          for (let i = 0; i < data.length; i++) {
            let fandom = data[i];
            let overflowFandom = find(overflow, f => {
              return f.name == fandom.name;
            });
            if (!fandom.has_prompts && overflowFandom.has_prompts) {
              data[i].has_prompts = true;
            }
          }
          this.$store.commit("setHasPrompts", data);
        } else {
          this.$store.commit("setHasPrompts", res.data);
        }
      });

    axios
      .get(
        `https://autoao3.herokuapp.com/db/exchanges/${exchangeId}/status`
      )
      .then(res => {
        this.$store.commit(
          "setIsExchangeOpen",
          res.data && (res.data.status == "open" || res.data.status == "processing")
        );
      });

    data.getExchangeFandoms(exchangeId)
      .then(result => {

        this.$store.commit("setFandoms", result);
        this.$store.commit("setDbLoaded", true);


      });

  },
  watch: {
    async $route(to) {
      if (to.name === "bookmarks") {
        await this.redirectExchangeId();

        this.$store.commit("setPrompts", {});
        this.$store.commit("setAggregatedPrompts", {});
      }
    },
    exchangeId(val) {
      if (this.$route.params.exchangeId != val) {
        this.$router.replace({
          name: this.$route.name,
          params: {
            exchangeId: val
          }
        });
      }
      this.loadFandomBookmarksAndPromptmarks();
    }
  },
  computed: {
    ...mapGetters([
      "exchangeId",
      "options",
      "hasPrompts",
      "fandoms",
      "letters",
      // "bookmarks",
      "isExchangeOpen",
      "characters",
      "aggregatedPromptmarks",
      "promptmarks",
      "prompts"
    ]),
    bookmarks: {
      get() {
        // remap fandom bookmarks by name instead of id against the up to date fandoms list
        // in case tags have shifted around
        return compact(
          map(this.$store.state.bookmarks, fandom => {
            return this.getFandom(fandom.name);
          })
        );
      },
      set(value) {
        if (!value) {
          return;
        }

        this.$store.commit("setBookmarks", value);
        this.$localStorage.set(
          `${this.exchangeId}bookmarks`,
          JSON.stringify(value)
        );
      }
    },
    promptmarks: {
      get() {
        return this.$store.state.promptmarks;
      },
      set(value) {
        if (!value) {
          return;
        }
        this.$store.commit("setPromptmarks", value);
        this.$localStorage.set(
          `${this.exchangeId}promptmarks`,
          JSON.stringify(value || [])
        );
      }
    },
    freeformmarks: {
      get() {
        return this.$store.state.freeformmarks;
      },
      set(value) {
        if (!value) {
          return;
        }
        this.$store.commit("setFreeformmarks", value);
        this.$localStorage.set(
          `${this.exchangeId}freeformmarks`,
          JSON.stringify(value || [])
        );
      }
    },
    relationshipmarks: {
      get() {
        return this.$store.state.relationshipmarks;
      },
      set(value) {
        if (!value) {
          return;
        }
        this.$store.commit("setRelationshipmarks", value);
        this.$localStorage.set(
          `${this.exchangeId}relationshipmarks`,
          JSON.stringify(value || [])
        );
      }
    },
    aggregatedPromptmarks: {
      get() {
        return this.$store.state.aggregatedPromptmarks;
      },
      set(value) {
        if (!value) {
          return;
        }
        this.$store.commit("setAggregatedPromptmarks", value);
        this.$localStorage.set(
          `${this.exchangeId}aggregatedpromptmarks`,
          JSON.stringify(value || [])
        );
      }
    },
    marks() {
      const marks = {
        fandoms: this.bookmarks,
        prompts: this.promptmarks,
        freeforms: this.freeformmarks,
        relationships: this.relationshipmarks,
        aggregated: this.aggregatedPromptmarks
      };

      return JSON.stringify(marks);
    }
  },
  data() {
    return {
      togglePromptText: true,
      showbackup: false,
      hideCharacters: true,
      hideLetters: false,
      saved: null,
      showLoad: false,
      letterChars: [],
      showLettersSection: true,
      showFandomsSection: true,
      showFreeformsSection: true,
      showRelationshipsSection: true,
      showAggregatedPromptsSection: true,
      showPromptsSection: true,
      showPrompt: [],
      togglePrompts: true,
      freeformPrompts: [],
      relationshipPrompts: [],
    };
  },
  methods: {
    ...utils,
    getFreeformPrompts(tag) {
      const id = cyrb53(tag).toString();
      return this.freeformPrompts[id];
    },
    getRelationshipPrompts(tag) {
      const id = cyrb53(tag).toString();
      return this.relationshipPrompts[id];
    },
    async remoteLoadFreeformPrompts(tag) {
      if (!this.exchangeId) {
        return;
      }
      const id = cyrb53(tag).toString();
      let newVal = this.freeformPrompts;
      this.freeformPrompts = {};

      newVal[id] = "loading";
      this.freeformPrompts = newVal;

      let requests;
      const escapedTag = encodeURIComponent(tag);
      const exchange = encodeURIComponent(this.exchangeId);

      requests = await data.getExchangeFreeformRequests(
        exchange,
        escapedTag
      );
      this.freeformPrompts = {};

      newVal[id] = requests;
      this.freeformPrompts = newVal;
    },
    shouldShowFreeformPrompts(tag) {
      const id = cyrb53(tag).toString();

      return (
        this.freeformPrompts[id] &&
        this.freeformPrompts[id].length &&
        this.freeformPrompts[id] !== "loading"
      );
    },
    async remoteLoadRelationshipPrompts(tag) {
      if (!this.exchangeId) {
        return;
      }
      const id = cyrb53(tag).toString();
      let newVal = this.relationshipPrompts;
      this.relationshipPrompts = {};

      newVal[id] = "loading";
      this.relationshipPrompts = newVal;

      let requests;
      const escapedTag = encodeURIComponent(tag);
      const exchange = encodeURIComponent(this.exchangeId);

      requests = await data.getExchangeRelationshipRequests(
        exchange,
        escapedTag
      );
      this.relationshipPrompts = {};

      newVal[id] = requests;
      this.relationshipPrompts = newVal;
    },
    shouldShowRelationshipPrompts(tag) {
      const id = cyrb53(tag).toString();

      return (
        this.relationshipPrompts[id] &&
        this.relationshipPrompts[id].length &&
        this.relationshipPrompts[id] !== "loading"
      );
    },
    getFandom(name) {
      // key off of name instead of index for shifting tagsets
      let fandom = find(this.fandoms, f => {
        return f.name === name;
      });

      return fandom;
    },
    loadSaved() {
      if (
        !confirm(
          "Are you sure you want to load bookmarks? Your current bookmarks will be merged with the bookmarks you are loading in!"
        )
      ) {
        return;
      }

      if (!this.saved || !this.saved.length) {
        alert("Your bookmarks were invalid!");
      }

      let parsed;

      try {
        parsed = JSON.parse(this.saved);
      } catch (e) {
        alert("Your bookmarks were invalid!");
        return;
      }

      if (!this.saved || !this.saved.length) {
        return;
      }

      let mergedFandoms = union(parsed.fandoms, this.bookmarks);
      let mergedPrompts = union(parsed.prompts, this.promptmarks);
      let mergedFreeforms = union(parsed.freeforms, this.freeformmarks);
      let mergedAggregatedPrompts = union(parsed.aggregated, this.aggregatedPromptmarks);

      this.$localStorage.set(
        `${this.exchangeId}bookmarks`,
        JSON.stringify(mergedFandoms || [])
      );
      this.$localStorage.set(
        `${this.exchangeId}promptmarks`,
        JSON.stringify(mergedPrompts || [])
      );
      this.$localStorage.set(
        `${this.exchangeId}freeeformmarks`,
        JSON.stringify(mergedFreeforms || [])
      );
      this.$localStorage.set(
        `${this.exchangeId}aggregatedpromptmarks`,
        JSON.stringify(mergedAggregatedPrompts || [])
      );
      this.$store.commit("setAggregatedPromptmarks", mergedAggregatedPrompts || []);
      this.$store.commit("setPromptmarks", mergedPrompts || []);
      this.$store.commit("setBookmarks", mergedFandoms || []);
    },
    cyrb53
  }
};
</script>

<style lang="scss" scoped>
.bookmarks {
  table th {
    width: auto;

    &.fave {
      width: 20px;
    }
  }

  .prompts th.characters,
  .prompts th.fandom {
    width: 250px;
  }

  .move {
    padding: 2px 10px 2px 0px;
    margin-right: 3px;
    cursor: move !important;
  }

  th.fandom {
    width: 33%;
  }

  .letters {
    li {
      padding-left: 10px;
    }
  }

  .options,
  .letters,
  .fandoms {
    margin-top: 10px;
  }

  .counts {
    font-size: 13px;
    margin-left: 22px;
  }

  .remove {
    color: #e74c3c;

    &:hover {
      color: #e4a6a6;
    }
  }

  .show-load {
    cursor: pointer;
  }

  .load-wrapper {
    margin-bottom: 10px;
    display: flex;
    flex-wrap: wrap;
  }

  textarea {
    margin-left: 5px;
    vertical-align: top;
    width: 150px;
  }

  .load {
    padding: 7px;
    font-weight: bold;
  }

  h3 .fas {
    vertical-align: middle;
  }
}

@media screen and (max-width: 767px) {}
</style>
